import {
  Brick,
  registerBrick,
  Query,
  Predicate,
  Transaction,
  ErrorFlow,
  Config
} from "olympe";
import BCP from "../../../common/section_bcp/DM_BCP";
import { getLogger, executeLambda } from '@olympeio/core';
import { getTerminatedRequests } from "../../section_risk/RiskCrisis";
import {
  isNumeric,
  validateEmail,
  getUserNameForStakeHolders,
  getFullName,
  zeroPad,
  zero4LeftPad,
  padLeftWithZerosFinalDigits,
  TARGET_BCP_REVERSE_MAP,
  getKey,
} from "../../section_bcp/mass_import/utils";

export default class ValidateMassImportExcelPhase2 extends Brick {
  /**
   * @override
   * @protected
   * @param {!BrickContext} $
   * @param {*} excelData
   * @param {string} graphToken
   * @param {*} fetchBcpPlantMaterialSpec
   * @param {*} fetchAlternativeVendor
   * @param {function()} forwardEvent
   * @param {function(*)} setOutput
   * @param {function(*)} setError
   * @param {function(Map)} setOriginalExcelData
   * @param {function(*)} setUpdateArray
   */
  async update(
    $,
    [excelData, graphToken, fetchBcpPlantMaterialSpec, fetchAlternativeVendor],
    [forwardEvent, setOutput, setError, setOriginalExcelData, setUpdateArray]
  ) {
    const logger = getLogger('ValidateMassImportExcelPhase2');
    const plant_id_length = 4;
    const material_id_length = 18;
    const specification_length = 12;
    const alternative_Vendor_Length1 = 9;
    const alternative_Vendor_Length2 = 10;
    const errorLogs = [];
    const finalSet = new Set();
    const updatedBcpSet = new Set();
    const origExcelData = new Map();
   

    // Process in batch (single gateway call per batch)

    async function getVendorListData(batch) {
      try {
        const customErrorCode = 2311052908;
        const vendorList = await executeLambda(
          $,
          fetchAlternativeVendor,
          [batch],
          customErrorCode
        );
        if (vendorList instanceof ErrorFlow) {
          const msg = vendorList.getMessage();
          $.set("Error information", { code: 2311492908, message: msg });
          $.trigger("Open Error Popup");
          logger.error(`Alternative Vendor Dropdown error: ${msg}`);
          throw new Error(
            `Error in fetching alternative vendor site data: ${msg}`
          );
        }
        return JSON.parse(vendorList).results;
      } catch (error) {
        throw new Error(`Error in fetching BCP data: ${error.message}`);
      }
    }
    // This function gets the requestor name, this is varying based on Dev/pre-prod
    let getRequestorName = (stakeHolderNameArray, environment) => {
      if (environment === "dev") {
        if (stakeHolderNameArray[2]?.length > 0)
          return getFullName(stakeHolderNameArray[2][0])
        else return "";
      } else {
        return (getFullName(stakeHolderNameArray[2][0],false))
      }
    }

    async function processInBatches(data, batchSize = 100) {
      for (let i = 0; i < data.length; i += batchSize) {
        const batch = data.slice(i, i + batchSize);
        logger.info(`Processing batch ${i / batchSize + 1}: IDs ${batch
          .map((e) => e)
          .join(", ")}`);
        // Fetch matching data from the database
        try {
          // Execute the lambda function to fetch the data
          const bodyResult = await executeLambda($, fetchBcpPlantMaterialSpec, [
            batch,
          ]);

          return bodyResult;
        } catch (error) {
          // Handle errors during the data fetch process
          throw new Error(`Error in fetching BCP data: ${error.message}`);
        } finally {
          // Validate the alternative vendor
        }
      }
    }

    // validate excel data
    if (excelData && Array.isArray(excelData) && excelData.length > 0) {
      const validItems = [];
      for (const item of excelData) {
        const errors = [];

        const plantId = item["Plant ID *"] ? String(item["Plant ID *"]).trim() : "";
        const materialId = item["Material ID *"] ? String(item["Material ID *"]).trim() : "";
        const specification = item["Specification"] ? String(item["Specification"]).trim() : "";
        const priority = item["Priority *"];
        const targetBCP = TARGET_BCP_REVERSE_MAP[item["Target BCP *"]];
        const GHGReduction = item["GHG reduction"];
        const costSavings = item["Any cost savings?"];
        const inputSaving = item["Input saving est. [CHF] :"];
        const remark = item["Remark"];
        const alternativeVS = item["Alternative VS"];
        const mvLinked = item["MV Linked"];
        const currentBCPStatus = item["Current BCP Status*"];
        const requestorName = item["Requestor Name*"];
        const bp = item["BP*"];
        const buyerName = item["Buyer Name*"];
        const maguire = item["Maguire Y/N"];
        const spendCat1 = item["Spend Cat L1"];
        const oscarID = item["Oscar ID"];
        logger.info(
          "*** Details from excel sheet latest ****",
          plantId,
          GHGReduction,
          mvLinked,
          alternativeVS,
          costSavings,
          inputSaving,
          materialId,
          priority,
          targetBCP,
          specification,
          currentBCPStatus,
          requestorName,
          bp,
          buyerName,
          remark,
          maguire,
          spendCat1,
          oscarID
        );

        //validate plantID 4 digit/alphanumeric string
        if (
          !item.hasOwnProperty("Plant ID *") &&
          plantId.length !== plant_id_length &&
          !plantId.match(/^[A-Za-z0-9_.]+$/)
        ) {
          errors.push("Invalid PlantID");
        }

        // Validate alternate vendor list
        if (
          item.hasOwnProperty("Alternative VS") &&
          !(alternativeVS?.toString().length === alternative_Vendor_Length1 || alternativeVS?.toString().length === alternative_Vendor_Length2)
        ) {
          errors.push("Invalid Alternative Vendor Site");
        }
        //validate MaterialID <=18 digit/alphanumeric string
        if (
          !item.hasOwnProperty("Material ID *") ||
          String(materialId).length > material_id_length ||
          !isNumeric(materialId)
        ) {
          errors.push("Invalid Material ID");
        }
        //validate Specification = 12 digit string or null/undefined
        if (
          item.hasOwnProperty("Specification") &&
          !(
            (String(specification).length === specification_length &&
              isNumeric(specification)) ||
            !specification
          )
        ) {
          errors.push("Invalid Specification");
        }

        //validation for priority
        if (
          !item.hasOwnProperty("Priority *") &&
          (priority?.toLowerCase() !== "high" || priority?.toLowerCase() !== "low")
        ) {
          errors.push("Priority field is not provided");
        }
        // validation for input saving 
        if ((costSavings?.toLowerCase() === 'yes') && (!inputSaving || Number(inputSaving) < 0)) {
          errors.push("Input Savings value should be positive numeric value");
        } else if (costSavings?.toLowerCase() === 'no') {
          item["Input saving est. [CHF] :"] = inputSaving || null;
        }

        //validation for Target BCP
        if (
          !item.hasOwnProperty("Target BCP *") &&
          (targetBCP !== "alternateSiteNewVendor" ||
            targetBCP !== "alternativeSiteApprovedPlant" ||
            targetBCP !== "alternateSiteSameVendor")
        ) {
          errors.push("Target BCP is not valid");
        }

        //validate email ID is valid
        if (!(item["PS/BP email *"] && item["Requestor Email *"] && item["Buyer Email *"]) ||
          !validateEmail(item["PS/BP email *"]) &&
          !validateEmail(item["Requestor Email *"]) &&
          !validateEmail(item["Buyer Email *"])
        ) {
          errors.push("Invalid PS/BP email or Buyer Email or Requestor Email");
        }

        // If no validation errors, proceed to fetch the plant materials & speciication details
        if (errors.length === 0) {
          //push item to validItems array for batch processing
          validItems.push(item);
        } else {
          errorLogs.push({
            ...item,
            Step: "Validation of BCP input data(invalid excel field)",
            Reason: errors.toString(),
          });
        }
      }

      if (validItems.length > 0) {
        // gateway call to fetch Vendor List
        let validVendorList = await getVendorListData(validItems);
        // gateway api call in batches
        const batchProcessingResult = await processInBatches(
          validItems,
          100
          //, fetchBcpPlantMaterialSpec
        );
        // Step 1: Build a Set of composite keys from batchProcessingResult
        const keySetOfBatchResult = new Set(batchProcessingResult.map(getKey));

        //
        for (const item of validItems) {
          const newItem = JSON.parse(JSON.stringify(item));
          const errors = [];
          //if record is found in gateway response then below condition is true, execute DB call followed by data transformation
          if (keySetOfBatchResult.has(getKey(item))) {
            const plantId = String(item["Plant ID *"]);
            const materialId = item["Material ID *"];
            const transformedMaterialId = zeroPad(materialId);
            const transformedPlantId = zero4LeftPad(plantId);

            const targetBCP = TARGET_BCP_REVERSE_MAP[item["Target BCP *"]];

            const queryResult = await Query.instancesOf(BCP)
              .filter(
                Predicate.and(
                  Predicate.equals(BCP.materialIdProp, transformedMaterialId),
                  Predicate.equals(BCP.plantIdProp, transformedPlantId)
                )
              )
              .execute();

            // if plantID & materialID combination does not exist, queryResult.toArray().length is 0
            const isUnique = queryResult.toArray().length === 0;

            // check workflowState of existing BCP request(based on plantID and materialID combination), If state is in (Completed, Rejected), then delete the existing request and proceed with creating new request else update the BCP request.
            if (!isUnique) {
              try {
                const finishedBCP = (
                  await getTerminatedRequests(
                    [{ materialId: transformedMaterialId, plantId: transformedPlantId }],
                    $
                  )
                )?.[0];
                if (
                  finishedBCP?.get(BCP.materialIdProp) ===
                  transformedMaterialId &&
                  finishedBCP?.get(BCP.plantIdProp) === transformedPlantId
                ) {
                  const t = new Transaction();
                  t.delete(finishedBCP);
                  await t.execute();
                } else {
                  updatedBcpSet.add(newItem);
                }
              } catch (e) {
                errors.push(
                  `Failed to get terminated request or delete existing requests: ${e.message}`
                );
              }
            }
            /*
                transformation of record fields to desired format
                requestor information, EITHER
                requestor given/first name and family/last/surname;requestor email
                BP (business partner/buyer) info: display name;email
                PM (procurement manager) info: display name;email
                materialId pad leading zeros untill 18 chars
              */
            newItem["Material ID *"] = transformedMaterialId;
            newItem["Plant ID *"] = transformedPlantId
            newItem["Target BCP *"] = targetBCP;
            try {
              const environment = Config.getParameter("ENV") || "";
              const stakeHolderNameArray = await getUserNameForStakeHolders(
                item["PS/BP email *"],
                item["Buyer Email *"],
                item["Requestor Email *"],
                item["Application Group email"],
                item["Quality in Factory Email"],
                item["Supplier Quality Assurance Email"],
                item["Procurement Specialist Email"],
                graphToken
              );
              let bpFullName,
                pmFullName,
                requestorFullName,
                agFullName,
                qaInfactoryFullName,
                sqaFullName,
                psFullName = "";
              if (stakeHolderNameArray?.error) {
                errors.push(
                  "Failed to fetch PS/BP or Buyer or Requestor details"
                );
              } else {
                bpFullName =
                  stakeHolderNameArray[0]?.length > 0
                    ? getFullName(stakeHolderNameArray[0][0])
                    : "";
                pmFullName =
                  stakeHolderNameArray[1]?.length > 0
                    ? getFullName(stakeHolderNameArray[1][0])
                    : "";
                requestorFullName = getRequestorName(stakeHolderNameArray, environment)
                console.log(" environment, requestor name", environment, requestorFullName)
                agFullName =
                  stakeHolderNameArray[3]?.length > 0
                    ? getFullName(stakeHolderNameArray[3][0])
                    : "";
                qaInfactoryFullName =
                  stakeHolderNameArray[4]?.length > 0
                    ? getFullName(stakeHolderNameArray[4][0])
                    : "";
                sqaFullName =
                  stakeHolderNameArray[5]?.length > 0
                    ? getFullName(stakeHolderNameArray[5][0])
                    : "";
                psFullName =
                  stakeHolderNameArray[6]?.length > 0
                    ? getFullName(stakeHolderNameArray[6][0])
                    : "";
              }
              if (
                typeof bpFullName === "string" &&
                typeof pmFullName === "string" &&
                typeof requestorFullName === "string"
              ) {
                newItem["PS/BP email *"] = bpFullName;
                newItem["Buyer Email *"] = pmFullName;
                newItem["Requestor Email *"] = requestorFullName;
                newItem["Application Group email"] = agFullName || "";
                newItem["Quality in Factory Email"] = qaInfactoryFullName || "";
                newItem["Supplier Quality Assurance Email"] = sqaFullName || "";
                newItem["Procurement Specialist Email"] = psFullName || "";
              } else {
                errors.push(
                  "Failed to fetch PS/BP or Buyer or Requestor details"
                );
              }

              // This following condition checked for valid alternative vendor
              if (newItem["Alternative VS"]) {
                const transformedAltVendor = padLeftWithZerosFinalDigits(newItem["Alternative VS"], 10);
                logger.info("::: Transformed Alternative Vendor :::", transformedAltVendor)
                let validVendors = validVendorList?.map(
                  (vendor) => vendor.vendorSite
                );
                let vendorSiteIndex = validVendors.findIndex(
                  (vendor) => vendor === transformedAltVendor
                );
                if (vendorSiteIndex === -1) {
                  logger.info("Invalid Aleternative vendor Id", newItem["Alternative VS"], transformedAltVendor)
                  errors.push(
                    "Please provide a valid Alternative Vendor Site Id"
                  );
                } else {
                  logger.info("Valid alternative vendor Id, transformed Id", newItem["Alternative VS"], transformedAltVendor)
                }
              }
              // End
            } catch (e) {
              logger.error("Failed to validateexcel mass upload excel data", e);
            }
            // if item is not present in updatedBcpSet then it is a create request else it will be update request
            //why taking set???
            if (
              errors.length === 0 &&
              ![...updatedBcpSet].some(
                (value) =>
                  String(value["Material ID *"]) ===
                  String(transformedMaterialId) &&
                  String(value["Plant ID *"]) === transformedPlantId
              )
            ) {
              finalSet.add(newItem);
            }
            // Add plantID + transformed_materialId as key and item as value to the origExcelData Map in order to generate errorLogs in Creation phase
            const cloneOfItem = (({ isPresent, ...rest }) => rest)(item); // remove isPresent property
            origExcelData.set(transformedPlantId + transformedMaterialId, cloneOfItem);
          } else {
            errors.push(
              "No match found for plant/material/specification combination in bcpStatus table"
            );
          }
          if (errors.length > 0) {
            errorLogs.push({
              ...item,
              Step: "Validation of BCP input data",
              Reason: errors.toString(),
            });
          }
        }
      }
    }
    setOutput(Array.from(finalSet));
    setError(Array.from(errorLogs));
    setOriginalExcelData(origExcelData);
    setUpdateArray(Array.from(updatedBcpSet));
    forwardEvent();
  }
}
registerBrick("019648ee1fc8df3fcd5d", ValidateMassImportExcelPhase2);
